import React from 'react';
import { useTheme, themeOptions } from '@/lib/theme-context';
import { Button } from '@/components/ui/button';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { Palette } from 'lucide-react';
import { cn } from '@/lib/utils';
import { playSound } from '@/lib/animations';
import { Card } from './card';

// Component for theme selection in menus, headers, etc.
export function MiniThemeSelector() {
  const { currentTheme, setTheme } = useTheme();

  const handleThemeChange = (themeName: string) => {
    setTheme(themeName);
    try {
      playSound('success');
    } catch (error) {
      console.error("Failed to play sound:", error);
    }
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="icon" className="focus-visible btn-animated">
          <Palette className="h-5 w-5" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        {Object.keys(themeOptions).map((themeName) => (
          <DropdownMenuItem
            key={themeName}
            onClick={() => handleThemeChange(themeName)}
            className={cn(
              "flex items-center gap-2 cursor-pointer",
              currentTheme.name === themeName && "bg-accent"
            )}
          >
            <div 
              className="w-4 h-4 rounded-full" 
              style={{ backgroundColor: themeOptions[themeName].primary }}
            />
            <span className="capitalize">{themeName}</span>
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

// Full theme selection component for dedicated theme pages
export function ThemeSelector() {
  const { currentTheme, setTheme, availableThemes } = useTheme();

  const handleThemeChange = (themeName: string) => {
    setTheme(themeName);
    try {
      playSound('success');
    } catch (error) {
      console.error("Failed to play sound:", error);
    }
  };

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      {availableThemes.map((theme) => (
        <Card 
          key={theme.name}
          className={cn(
            "p-4 cursor-pointer hover:shadow-md transition-shadow duration-200",
            currentTheme.name === theme.name && "ring-2 ring-primary"
          )}
          onClick={() => handleThemeChange(theme.name)}
        >
          <div className="flex items-start gap-3">
            {/* Color circle */}
            <div 
              className="w-12 h-12 rounded-full flex-shrink-0 mt-1" 
              style={{ backgroundColor: theme.primary }}
            />
            
            <div className="flex-1">
              {/* Theme name */}
              <div className="flex justify-between items-center mb-2">
                <h3 className="font-medium capitalize">{theme.name}</h3>
                {currentTheme.name === theme.name && (
                  <span className="text-sm text-primary font-medium">Current</span>
                )}
              </div>
              
              {/* Theme description */}
              <p className="text-muted-foreground text-sm mb-2">{theme.description}</p>
              
              {/* Color swatches */}
              <div className="flex space-x-1 mt-3">
                <div className="w-6 h-6 rounded" style={{ backgroundColor: theme.primary }}></div>
                <div className="w-6 h-6 rounded" style={{ backgroundColor: theme.accent }}></div>
                <div className="w-6 h-6 rounded" style={{ backgroundColor: theme.background }}></div>
                <div className="w-6 h-6 rounded border" style={{ backgroundColor: theme.card }}></div>
              </div>
            </div>
          </div>
        </Card>
      ))}
    </div>
  );
}